import { useState } from "react";
import { Check, Clock, X, Minus, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * QbSyncChip — compact status icon shown next to each synced row.
 *
 * `kind` controls both the API path and which fields on the record are read:
 *   invoice   → POST /integrations/quickbooks/sync/invoice/{id}        fields: qb_invoice_*
 *   payment   → POST /integrations/quickbooks/sync/payment/{id}        fields: qb_payment_*
 *   expense   → POST /integrations/quickbooks/sync/expense/{id}        fields: qb_bill_*
 *   pay       → POST /integrations/quickbooks/sync/contractor-pay/{id} fields: qb_bill_*
 *
 * `record` is any SION row object that has an `id`.
 * (kept the legacy `quote` alias as an alternative prop name for back-compat with the Invoices page.)
 */
const KIND_CONFIG = {
  invoice: { endpoint: "invoice", prefix: "qb_invoice", label: "QB invoice sync" },
  payment: { endpoint: "payment", prefix: "qb_payment", label: "QB payment sync" },
  expense: { endpoint: "expense", prefix: "qb_bill", label: "QB bill sync (expense)" },
  pay: { endpoint: "contractor-pay", prefix: "qb_bill", label: "QB bill sync (contractor pay)" },
};

export default function QbSyncChip({ record, quote, kind = "invoice", onSynced }) {
  const row = record || quote || {};
  const [busy, setBusy] = useState(false);
  const cfg = KIND_CONFIG[kind] || KIND_CONFIG.invoice;
  const statusField = `${cfg.prefix}_status`;
  const errorField = `${cfg.prefix}_error`;
  const tsField = `${cfg.prefix}_synced_at`;
  const qbIdField = kind === "invoice" ? "qb_invoice_id" : `${cfg.prefix}_qb_id`;
  const status = row[statusField] || "";
  const title = [
    cfg.label,
    status ? `Status: ${status.replace(/_/g, " ")}` : "Status: not synced",
    row[tsField] ? `When: ${new Date(row[tsField]).toLocaleString("en-GB")}` : null,
    row[qbIdField] ? `QB ID: ${row[qbIdField]}` : null,
    row[errorField] ? `Error: ${row[errorField]}` : null,
  ].filter(Boolean).join("\n");

  const retry = async (e) => {
    e.stopPropagation();
    if (busy) return;
    if (status === "synced") return;
    setBusy(true);
    try {
      const r = await api.post(`/integrations/quickbooks/sync/${cfg.endpoint}/${row.id}`);
      if (r.data?.ok) {
        const qid = r.data.qb_invoice_id || r.data.qb_payment_id || r.data.qb_bill_id;
        toast.success(`Synced to QuickBooks${qid ? ` (#${qid})` : ""}`);
        onSynced && onSynced();
      } else if (r.data?.skipped) {
        toast.message(r.data.reason || r.data.status || "Skipped");
      } else {
        toast.error(r.data?.error || "Sync failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    } finally {
      setBusy(false);
    }
  };

  const baseCls = "inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded transition-colors";
  if (busy) {
    return <span className={baseCls} title={title} style={{ background: "rgba(245,158,11,0.12)", color: "#92400E" }} data-testid={`qb-sync-${kind}-busy-${row.id}`}><Loader2 className="w-3 h-3 animate-spin" /></span>;
  }
  if (status === "synced") {
    return <span className={baseCls} title={title} style={{ background: "rgba(22,163,74,0.12)", color: "#15803D" }} data-testid={`qb-sync-${kind}-synced-${row.id}`}><Check className="w-3 h-3" /></span>;
  }
  if (status === "pending") {
    return <span className={baseCls} title={title} style={{ background: "rgba(245,158,11,0.12)", color: "#92400E" }} data-testid={`qb-sync-${kind}-pending-${row.id}`}><Clock className="w-3 h-3" /></span>;
  }
  if (status === "failed") {
    return (
      <button onClick={retry} className={baseCls} title={title + "\nClick to retry"} style={{ background: "rgba(220,38,38,0.12)", color: "#991B1B", cursor: "pointer" }} data-testid={`qb-sync-${kind}-failed-${row.id}`}>
        <X className="w-3 h-3" /><RefreshCw className="w-3 h-3" />
      </button>
    );
  }
  if (status === "skipped_before_start_date" || status === "skipped") {
    return <span className={baseCls} title={title} style={{ background: "rgba(100,116,139,0.12)", color: "var(--text-muted)" }} data-testid={`qb-sync-${kind}-skipped-${row.id}`}><Minus className="w-3 h-3" /></span>;
  }
  return (
    <button onClick={retry} className={baseCls} title={title + "\nClick to push to QuickBooks"} style={{ background: "var(--bg)", color: "var(--text-muted)", border: "1px solid var(--border)", cursor: "pointer" }} data-testid={`qb-sync-${kind}-none-${row.id}`}>
      <Minus className="w-3 h-3" />
    </button>
  );
}
